"use client";

import * as React from "react";
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import { cn } from "@/lib/utils";

const ToggleGroupContext = React.createContext<{
  size?: "default" | "sm" | "lg";
  variant?: "default" | "outline";
}>({
  size: "default",
  variant: "default",
});

function ToggleGroup({
  ref,
  className,
  size = "default",
  variant = "default",
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> & {
  size?: "default" | "sm" | "lg";
  variant?: "default" | "outline";
}) {
  return (
    <ToggleGroupPrimitive.Root
      ref={ref}
      className={cn("flex items-center justify-center gap-1", className)}
      {...props}
    >
      <ToggleGroupContext value={{ size, variant }}>
        {children}
      </ToggleGroupContext>
    </ToggleGroupPrimitive.Root>
  );
}

const toggleGroupItemVariants = {
  default:
    "bg-transparent data-[state=on]:bg-action-tint data-[state=on]:text-action-strong",
  outline:
    "border border-input bg-transparent data-[state=on]:border-action data-[state=on]:bg-action-tint data-[state=on]:text-action-strong",
};

const toggleGroupItemSizes = {
  default: "h-control px-3",
  sm: "h-control-sm px-2",
  lg: "h-control px-4",
};

function ToggleGroupItem({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item>) {
  const context = React.use(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      data-slot="toggle-group-item"
      className={cn(
        "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors",
        "data-[state=off]:hover:bg-muted/50 data-[state=off]:hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        toggleGroupItemVariants[context.variant || "default"],
        toggleGroupItemSizes[context.size || "default"],
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
}

export { ToggleGroup, ToggleGroupItem };
